import { isAnyOf } from '@reduxjs/toolkit';
import { lastValueFrom } from 'rxjs';

import {
  getUser, getUserError, getUserSuccess,
  updateUser, updateUserError, updateUserSuccess,
  updateUserAddress, updateUserAddressSuccess, updateUserAddressError,
  deleteUserAddress, deleteUserAddressSuccess, deleteUserAddressError,
  updateUserPassword, updateUserPasswordSuccess, updateUserEmail, updateUserEmailSuccess, updateUserSetting,
  updateLoyaltyProgram,
  updateUserCommunicationPreferences,
  updateUserAddressAndValidateInExproOrder
} from '../slices/user.slice';
import { connectUserError, connectedUserError, createUserSuccess, disconnectUserSuccess } from '../slices/auth.slice';
import { updateCartAddresses, updateCartDeletedAddress } from '../slices/cart.slice';
import { setSelectedAddress, setValidateSelectedAddress } from '../slices/expro.slice';
import { hidePageProgress, showPageProgress } from '../slices/gui.slice';
import { addApiErrorToast, addToast } from '../slices/toast.slice';
import { AddressModel, UserModel } from '@/models';
import { AppListenerEntry } from '@/redux';
import { subscribeToNewsletter } from '@/redux/epics/newsletter.epics';
import { LocalStorageService } from '@/services/LocalStorage.service';
import { KEYS } from '@/constants/keys';

export const epicGetUser: AppListenerEntry = {
  actionCreator: getUser,
  effect: async (_action: ReturnType<typeof getUser>, { getState, dispatch, extra: { userService } }) => {
    try {
      const storeId = getState().root.storeConf.storeId;
      const user = await lastValueFrom(userService.get(storeId)) as UserModel;
      dispatch(getUserSuccess({ user }));
    } catch (error) {
      dispatch(getUserError({ error }));
    }
  }
};

export const epicUpdateUser: AppListenerEntry = {
  actionCreator: updateUser,
  effect: async ({ payload }: ReturnType<typeof updateUser>, { dispatch, extra: { userService, authService } }) => {
    const { user } = payload;
    if (user?._id && user?.email) {
      try {
        const update = await lastValueFrom(userService.update(user)) as UserModel;
        dispatch(updateUserSuccess({ user: update }));
      } catch (error) {
        dispatch(updateUserError({ error }));
      }
    } else {
      authService.disconnect();
      dispatch(updateUserError({ error: new Error('Bad user') }));
    }
  }
};

export const epicUpdateUserAddress: AppListenerEntry = {
  actionCreator: updateUserAddress,
  effect: async ({ payload }: ReturnType<typeof updateUserAddress>, { getState, dispatch, extra: { userService } }) => {
    try {
      let idToSelect = payload?.address?.idNext;
      const addressesIdNextBeforeUpdate: string[] = getState().user?.content?.addresses?.map((addr: AddressModel) => addr.idNext);
      const update = await lastValueFrom(userService.updateAddress(payload.address)) as UserModel;
      const addressesIdNextAfterUpdate: string[] = update?.addresses?.map((addr: AddressModel) => addr.idNext);
      if (!idToSelect) {
        const newIds = addressesIdNextAfterUpdate?.filter(
          (id) => !addressesIdNextBeforeUpdate?.includes(id)
        );
        idToSelect = newIds ? newIds[0] : null;
      }
      dispatch(updateUserAddressSuccess({ user: update, idToSelect }));
      dispatch(updateCartAddresses({ user: update }));
    } catch (error) {
      dispatch(updateUserAddressError({ error }));
    }
  }
};
export const epicUpdateUserAddressAndValidateInExproOrder: AppListenerEntry = {
  actionCreator: updateUserAddressAndValidateInExproOrder,
  effect: async (
    { payload }: ReturnType<typeof updateUserAddressAndValidateInExproOrder>, { dispatch, extra: { userService } }
  ) => {
    try {
      dispatch(showPageProgress());
      const update = await lastValueFrom(userService.updateAddress(payload.address)) as UserModel;
      // In order to update exproOrder.address
      if (payload.address.id) {
        dispatch(setSelectedAddress({ selectedAddress: payload.address }));
      } else {
        dispatch(setSelectedAddress({ selectedAddress: update.addresses[update.addresses.length - 1] }));
      }
      dispatch(setValidateSelectedAddress({ validateSelectedAddress: true }));

      dispatch(updateUserAddressSuccess({ user: update }));
      dispatch(updateCartAddresses({ user: update }));
      dispatch(hidePageProgress());
    } catch (error) {
      dispatch(updateUserAddressError({ error }));
    }
  }
};

export const epicDeleteUserAddress: AppListenerEntry = {
  actionCreator: deleteUserAddress,
  effect: async ({ payload }: ReturnType<typeof deleteUserAddress>, { dispatch, extra: { userService } }) => {
    try {
      const update = await lastValueFrom(userService.deleteAddress(payload.addressId)) as UserModel;
      dispatch(updateCartDeletedAddress({ addressId: payload.addressId }));
      dispatch(deleteUserAddressSuccess({ user: update }));
    } catch (error) {
      dispatch(deleteUserAddressError({ error }));
    }
  }
};

export const epicUpdateUserPassword: AppListenerEntry = {
  actionCreator: updateUserPassword,
  effect: async ({ payload }: ReturnType<typeof updateUserPassword>, { dispatch, extra: { userService } }) => {
    const { password, oldPassword } = payload;
    try {
      const update = await lastValueFrom(userService.updatePassword(password, oldPassword)) as UserModel;
      dispatch(updateUserSuccess({ user: update }));
      dispatch(updateUserPasswordSuccess());
    } catch (error) {
      dispatch(updateUserError({ error }));
    }
  }
};

export const epicUpdateUserEmail: AppListenerEntry = {
  actionCreator: updateUserEmail,
  effect: async ({ payload }: ReturnType<typeof updateUserEmail>, { dispatch, extra: { userService } }) => {
    const { password, email } = payload.content;
    try {
      const update = await lastValueFrom(userService.updateEmail(password, email)) as UserModel;
      dispatch(updateUserSuccess({ user: update }));
      dispatch(updateUserEmailSuccess());
    } catch (error) {
      dispatch(updateUserError({ error }));
    }
  }
};

export const epicUpdateUserSetting: AppListenerEntry = {
  actionCreator: updateUserSetting,
  effect: (_action: ReturnType<typeof updateUserSetting>, { getState }) => {
    LocalStorageService.setObject(KEYS.SETTINGS, getState().user.settings);
  }
};

export const epicDisconnectedUser: AppListenerEntry = {
  matcher: isAnyOf(
    connectedUserError,
    connectUserError,
    createUserSuccess,
    disconnectUserSuccess
  ),
  effect: (_action: ReturnType<any>, { dispatch }) => {
    dispatch(getUserSuccess({ user: undefined }));
  }
};

export const epicUpdateLoyaltyProgram: AppListenerEntry = {
  actionCreator: updateLoyaltyProgram,
  effect: async ({ payload }: ReturnType<typeof updateLoyaltyProgram>, { dispatch, extra: { userService } }) => {
    const { t } = payload;
    try {
      dispatch(showPageProgress());
      await lastValueFrom(userService.updateLoyaltyProgram());
      dispatch(addToast({ msg: t('admin:customerLoyaltyProgramCsr.loyaltyProgramUpdated'), err: false }));
    } catch (error) {
      dispatch(addApiErrorToast(error));
    } finally {
      dispatch(hidePageProgress());
    }
  }
};

export const epicUpdateUserCommunicationPreferences: AppListenerEntry = {
  actionCreator: updateUserCommunicationPreferences,
  effect: async ({ payload }: ReturnType<typeof updateUserCommunicationPreferences>, { dispatch, extra: { userService } }) => {
    const { storeId, langComm } = payload;
    try {
      const update = await lastValueFrom(userService.updateUserCommunicationPreferences(storeId, langComm)) as UserModel;
      dispatch(updateUserSuccess({ user: update }));
    } catch (error) {
      dispatch(updateUserError({ error }));
    }
  }
};

export const updateUserNewsLetterSubscriptionStatusListener: AppListenerEntry = {
  matcher: (action) => action.type === subscribeToNewsletter.fulfilled.type,
  effect: async (_, { getState, extra: { userService } }) => {
    const user = { ...getState().user.content, newsletter: true };
    return await lastValueFrom(userService.update(user));
  }
};
